import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import Button from '../../../components/Button';
import SectionHeading from '../../../components/SectionHeading';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
  EmptyTableState,
  LoadingTableState,
} from '../../../components/StandardTable';
import AdminListToolbar from '../../components/AdminListToolbar';
import ListPager from '../../components/ListPager';
import { AdminListResult, useAdminList } from '../../hooks/useAdminList';
import { formatDate } from '../../lib/format';
import { PeopleIcon } from '../../../components/icons';

const filterSelectClasses =
  'h-9 rounded-lg border border-border-light bg-white px-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary';

interface CollaboratorRow {
  workspace_member_id: string;
  role: string;
  status: string;
  invited_at: string | null;
  workspace_id: string;
  workspace_name: string;
  user_id: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  username: string | null;
  email: string | null;
  home_organizations: string | null;
}

const parseCollaborators = (raw: unknown): AdminListResult<CollaboratorRow> => {
  const data = (raw as {
    data?: { collaborators?: CollaboratorRow[]; pagination?: { total?: number; hasMore?: boolean } };
  }).data;
  return {
    rows: data?.collaborators ?? [],
    total: data?.pagination?.total ?? 0,
    hasMore: data?.pagination?.hasMore ?? false,
  };
};

const collaboratorName = (row: CollaboratorRow): string =>
  row.display_name ||
  [row.first_name, row.last_name].filter(Boolean).join(' ') ||
  row.email ||
  row.user_id;

const COLUMN_COUNT = 7;

/**
 * Workspace collaborators — people on the roster of a workspace this org
 * owns who are NOT staff of the org (staff of another org, or a citizen
 * affiliate invited in). Workspace access never creates org membership
 * (#1520), so they are absent from the membership table above; this lists
 * them with their home orgs. Read-only: access is granted and removed on the
 * workspace or the user's page.
 */
const OrgWorkspaceCollaborators: React.FC<{
  orgId: string;
  workspaces: Array<{ id: string; name: string }>;
}> = ({ orgId, workspaces }) => {
  const navigate = useNavigate();
  const [role, setRole] = useState('');
  const [inviteStatus, setInviteStatus] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');

  const list = useAdminList<CollaboratorRow>({
    queryKey: `org-workspace-collaborators-${orgId}`,
    endpoint: `/admin/accounts/organizations/${orgId}/workspace-collaborators`,
    parse: parseCollaborators,
    filters: {
      role: role || undefined,
      workspace_status: inviteStatus || undefined,
      workspace_id: workspaceId || undefined,
    },
    initialSort: { key: 'name', direction: 'asc' },
  });

  return (
    <section className="space-y-4" aria-labelledby="workspace-collaborators-heading">
      <SectionHeading
        id="workspace-collaborators-heading"
        note={list.isLoading ? undefined : `${list.total}`}
        right="On this org's workspaces, not org members"
      >
        Workspace collaborators
      </SectionHeading>

      <AdminListToolbar
        search={list.search}
        onSearch={list.setSearch}
        searchPlaceholder="Search collaborators by name or email…"
        searchAriaLabel="Search workspace collaborators"
      >
        <select
          value={role}
          onChange={(event) => setRole(event.target.value)}
          aria-label="Filter collaborators by workspace role"
          className={filterSelectClasses}
        >
          <option value="">All workspace roles</option>
          <option value="contributor">Contributor</option>
          <option value="guest">Guest</option>
          <option value="owner">Owner</option>
        </select>
        <select
          value={inviteStatus}
          onChange={(event) => setInviteStatus(event.target.value)}
          aria-label="Filter collaborators by invitation status"
          className={filterSelectClasses}
        >
          <option value="">Joined + invited</option>
          <option value="accepted">Joined</option>
          <option value="pending">Invited</option>
        </select>
        {workspaces.length > 1 && (
          <select
            value={workspaceId}
            onChange={(event) => setWorkspaceId(event.target.value)}
            aria-label="Filter collaborators by workspace"
            className={filterSelectClasses}
          >
            <option value="">All workspaces</option>
            {workspaces.map((workspace) => (
              <option key={workspace.id} value={workspace.id}>
                {workspace.name}
              </option>
            ))}
          </select>
        )}
      </AdminListToolbar>

      {list.isError ? (
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            Couldn't load workspace collaborators. Please try again.
          </p>
          <Button variant="outline" size="sm" onClick={() => list.refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <StandardTable horizontalScroll loading={list.isFetching && !list.isLoading}>
          <StandardTableHeader>
            <StandardTableRow>
              <StandardTableHeaderCell sortable sortKey="name" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Name
              </StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="md" sortable sortKey="email" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Email
              </StandardTableHeaderCell>
              <StandardTableHeaderCell>Home organization</StandardTableHeaderCell>
              <StandardTableHeaderCell sortable sortKey="workspace" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Workspace
              </StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="fit" sortable sortKey="role" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Role
              </StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="fit" sortable sortKey="status" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Status
              </StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="lg" columnType="fit" sortable sortKey="invited_at" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Invited
              </StandardTableHeaderCell>
            </StandardTableRow>
          </StandardTableHeader>
          <StandardTableBody>
            {list.isLoading ? (
              <LoadingTableState colSpan={COLUMN_COUNT} />
            ) : list.rows.length === 0 ? (
              <EmptyTableState
                colSpan={COLUMN_COUNT}
                message={list.search ? `No collaborators match "${list.search}"` : 'No outside collaborators on this organization\'s workspaces'}
                icon={<PeopleIcon className="h-8 w-8" />}
              />
            ) : (
              list.rows.map((row) => (
                <StandardTableRow
                  key={row.workspace_member_id}
                  onClick={() => navigate(`/admin-next/accounts/users/${row.user_id}`)}
                >
                  <StandardTableCell>
                    <div className="font-medium text-text-primary dark:text-text-dark-primary">
                      {collaboratorName(row)}
                    </div>
                    {row.username && (
                      <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">@{row.username}</div>
                    )}
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="md">{row.email ?? '—'}</StandardTableCell>
                  <StandardTableCell>{row.home_organizations || '—'}</StandardTableCell>
                  <StandardTableCell>
                    <Link
                      to={`/admin-next/accounts/workspaces/${row.workspace_id}`}
                      onClick={(event) => event.stopPropagation()}
                      className="text-primary-700 hover:underline dark:text-primary-400"
                    >
                      {row.workspace_name}
                    </Link>
                  </StandardTableCell>
                  <StandardTableCell>
                    <span className="rounded-full bg-background-secondary px-2 py-0.5 text-xs text-text-secondary dark:bg-background-dark-tertiary dark:text-text-dark-secondary">
                      {row.role}
                    </span>
                  </StandardTableCell>
                  <StandardTableCell>
                    <span className="text-xs text-text-secondary dark:text-text-dark-secondary">
                      {row.status === 'pending' ? 'Invited' : 'Joined'}
                    </span>
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="lg">
                    {row.invited_at ? formatDate(row.invited_at) : '—'}
                  </StandardTableCell>
                </StandardTableRow>
              ))
            )}
          </StandardTableBody>
        </StandardTable>
      )}

      {!list.isError && (
        <ListPager
          offset={list.offset}
          limit={list.limit}
          total={list.total}
          page={list.page}
          pageCount={list.pageCount}
          hasPrev={list.hasPrev}
          hasNext={list.hasNext}
          onPrev={list.prevPage}
          onNext={list.nextPage}
          noun="collaborators"
        />
      )}
    </section>
  );
};

export default OrgWorkspaceCollaborators;
